import React from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Minus, Plus } from 'lucide-react';
import { GRAPH_NODE_H, GRAPH_NODE_W, type GraphNodeData, type GraphNodeKind } from './policyGraphTypes';

export type PolicyGraphNodeData = GraphNodeData & {
  onToggleExpand?: () => void;
};

const KIND_LABEL: Record<GraphNodeKind, string> = {
  vault: 'DataVault',
  model: 'Data Model',
  policy: 'Policy',
  asset: 'Asset',
  column: 'Column',
  overflow: 'More',
};

const handleClass = '!pointer-events-none !h-1.5 !w-1.5 !border-0 !bg-neutral-400';

export default function PolicyGraphNode({ data, selected }: NodeProps<PolicyGraphNodeData>) {
  return (
    <div className="relative" style={{ width: GRAPH_NODE_W, height: GRAPH_NODE_H }}>
      <Handle id="left" type="target" position={Position.Left} className={handleClass} />
      <Handle id="left-source" type="source" position={Position.Left} className={handleClass} />
      <Handle id="right" type="source" position={Position.Right} className={handleClass} />
      <Handle id="right-target" type="target" position={Position.Right} className={handleClass} />
      <Handle id="top-target" type="target" position={Position.Top} className={handleClass} />
      <Handle id="top-source" type="source" position={Position.Top} className={handleClass} />
      <Handle id="bottom-target" type="target" position={Position.Bottom} className={handleClass} />
      <Handle id="bottom-source" type="source" position={Position.Bottom} className={handleClass} />
      <div
        className={`box-border h-full w-full overflow-hidden rounded-md border bg-white px-2.5 py-2 ${
          selected ? 'border-neutral-800' : data.origin === 'inherited' ? 'border-dashed border-neutral-400' : 'border-neutral-300'
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 truncate text-[10px] font-medium uppercase tracking-wide text-neutral-500">
            {data.origin === 'inherited' ? 'Inherited policy' : data.origin === 'local' ? 'Local policy' : KIND_LABEL[data.kind]}
          </p>
          {data.expandable ? (
            <button
              type="button"
              className="nodrag nopan relative z-10 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border border-neutral-300 text-neutral-600 hover:bg-neutral-50"
              aria-label={data.expanded ? 'Collapse' : 'Expand'}
              onMouseDown={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                data.onToggleExpand?.();
              }}
            >
              {data.expanded ? <Minus size={10} /> : <Plus size={10} />}
            </button>
          ) : null}
        </div>
        <p className="mt-1 truncate text-[13px] font-semibold leading-5 text-neutral-900">{data.title}</p>
        <p className="mt-0.5 truncate text-[11px] leading-4 text-neutral-500">{data.meta}</p>
      </div>
    </div>
  );
}
